// src/components/Store/patientReportsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../BaseUrl';

// API base URLs
const AUTH_API_URL = `${BaseUrl.authurl}Auth`;
const CONSULTATION_API_URL = `${BaseUrl.consultationurl}Consultations`;
// Correct lab URLs
const LAB_SERVICE_MASTER_API_URL = `${BaseUrl.labsurl}LabTestServices`;
const PACKAGE_MASTER_API_URL = `${BaseUrl.labsurl}Packages`;
const PACKAGE_SERVICES_API_URL = `${BaseUrl.labsurl}PackageServices`;

// Get Authorization Headers
const getHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'accept': 'text/plain',
    'Content-Type': 'application/json'
  };
};

// --- Async Thunks ---

// Search patients by name, ID, or phone
export const searchPatients = createAsyncThunk(
  'patientReports/searchPatients',
  async (searchQuery, { rejectWithValue }) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      return [];
    }

    try {
      const headers = getHeaders();
      if (!headers.Authorization) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(
        `${AUTH_API_URL}/GetPatients?id=${encodeURIComponent(searchQuery)}`,
        { headers }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to search patients');
    }
  }
);

// Fetch patient consultations with optional status filter
export const fetchPatientConsultations = createAsyncThunk(
  'patientReports/fetchPatientConsultations',
  async ({ mrn, status }, { rejectWithValue }) => {
    if (!mrn) {
      throw new Error('Patient MRN is required');
    }

    try {
      const headers = getHeaders();
      if (!headers.Authorization) {
        throw new Error('No authentication token found');
      }

      // Build URL with status parameter
      let url = `${CONSULTATION_API_URL}/GetPatientConsultations?MRN=${encodeURIComponent(mrn)}`;
      if (status && status !== '' && status !== 'All') {
        url += `&status=${status}`;
      }

      const response = await axios.get(url, { headers });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch consultations');
    }
  }
);

// Fetch lab service master data
export const fetchLabServiceMasterData = createAsyncThunk(
  'patientReports/fetchLabServiceMasterData',
  async (_, { rejectWithValue }) => {
    try {
      const headers = getHeaders();
      
      const response = await axios.get(LAB_SERVICE_MASTER_API_URL, { 
        headers,
        params: { _t: Date.now() }
      });
      
      // Normalize data into an object map for easy lookup: { id: serviceObject, ... }
      const normalizedData = response.data.reduce((acc, service) => {
        acc[service.id] = service;
        return acc;
      }, {});
      
      return normalizedData;
    } catch (error) {
      console.error('Error fetching lab services:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch lab service master data');
    }
  }
);

// Fetch package master data
export const fetchPackageMasterData = createAsyncThunk(
  'patientReports/fetchPackageMasterData',
  async (_, { rejectWithValue }) => {
    try {
      const headers = getHeaders();
      
      const response = await axios.get(PACKAGE_MASTER_API_URL, { 
        headers,
        params: { _t: Date.now() }
      });
      
      // Normalize data into an object map for easy lookup: { id: packageObject, ... }
      const normalizedData = response.data.reduce((acc, pkg) => {
        acc[pkg.id] = pkg;
        return acc;
      }, {});
      
      return normalizedData;
    } catch (error) {
      console.error('Error fetching packages:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch package master data');
    }
  }
);

// Fetch package services mapping (which services belong to which package)
export const fetchPackageServicesMapping = createAsyncThunk(
  'patientReports/fetchPackageServicesMapping',
  async (_, { rejectWithValue }) => {
    try {
      const headers = getHeaders();
      
      const response = await axios.get(PACKAGE_SERVICES_API_URL, { 
        headers,
        params: { _t: Date.now() }
      });
      
      // Create mapping: { packageId: [serviceId1, serviceId2, ...] }
      const mapping = response.data.reduce((acc, item) => {
        if (!acc[item.packageId]) {
          acc[item.packageId] = [];
        }
        acc[item.packageId].push(item.serviceId);
        return acc;
      }, {});
      
      return mapping;
    } catch (error) {
      console.error('Error fetching package services mapping:', error);
      return {};
    }
  }
);

// Fetch consultation details by ID with enriched lab test and package data
export const fetchConsultationDetails = createAsyncThunk(
  'patientReports/fetchConsultationDetails',
  async ({ consultationId, consultationType }, { getState, rejectWithValue }) => {
    if (!consultationId) {
      throw new Error('Consultation ID is required');
    }

    try {
      const headers = getHeaders();
      if (!headers.Authorization) {
        throw new Error('No authentication token found');
      }

      const url = `${CONSULTATION_API_URL}/GetConsultationById?ConsultationId=${encodeURIComponent(consultationId)}&Type=${consultationType}`;
      
      const response = await axios.get(url, { headers });
      const consultationData = response.data;
      
      // Get master data from state
      const { labServiceMaster, packageMaster, packageServicesMapping } = getState().patientReports;
      
      // Enrich PatientServiceList with service names from master data
      if (consultationData.PatientServiceList && consultationData.PatientServiceList.length > 0) {
        consultationData.PatientServiceList = consultationData.PatientServiceList.map(service => {
          const masterService = labServiceMaster[service.ServiceId];
          return {
            ...service,
            Id: service.Id || service.id,
            ServiceId: service.ServiceId,
            ServiceName: masterService?.serviceName || masterService?.name || `Service ID: ${service.ServiceId}`,
            Description: masterService?.description || '',
            Category: masterService?.category || '',
            UnitOfMeasure: masterService?.unitOfMeasure || '',
            NormalRange: masterService?.minVal && masterService?.maxVal 
              ? `${masterService.minVal} - ${masterService.maxVal} ${masterService.unitOfMeasure || ''}`
              : 'N/A',
            Result: service.Result || 0,
            Status: service.Status || service.ServiceStatus || 'pending',
            CompletionDate: service.CompletionDate
          };
        });
      }
      
      // Enrich PatientPackageList with package names and their services
      if (consultationData.PatientPackageList && consultationData.PatientPackageList.length > 0) {
        consultationData.PatientPackageList = consultationData.PatientPackageList.map(pkg => {
          const masterPackage = packageMaster[pkg.PackageId];
          const packageServiceIds = packageServicesMapping[pkg.PackageId] || [];
          
          // Get all services for this package
          const packageServices = packageServiceIds.map(serviceId => {
            const service = labServiceMaster[serviceId];
            return service ? {
              id: service.id,
              serviceName: service.serviceName,
              description: service.description,
              category: service.category,
              unitOfMeasure: service.unitOfMeasure,
              normalRange: service.minVal && service.maxVal 
                ? `${service.minVal} - ${service.maxVal} ${service.unitOfMeasure || ''}`
                : 'N/A'
            } : null;
          }).filter(s => s !== null);
          
          return {
            ...pkg,
            Id: pkg.Id || pkg.id,
            PackageId: pkg.PackageId,
            PackageName: masterPackage?.name || masterPackage?.packageName || `Package ID: ${pkg.PackageId}`,
            PackageDescription: masterPackage?.description || '',
            PackagePrice: masterPackage?.price || 0,
            Services: packageServices,
            Status: pkg.Status || pkg.PackageStatus || 'pending',
            CompletionDate: pkg.CompletionDate
          };
        });
      }
      
      return consultationData;
    } catch (error) {
      console.error('Error fetching consultation details:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch consultation details');
    }
  }
);

// --- Initial State ---
const initialState = {
  // Patient search
  searchTerm: '',
  patients: [],
  searchResults: [],
  loading: 'idle',
  error: null,
  
  // Selected patient
  selectedPatient: null,
  
  // Consultations
  consultations: [],
  consultationLoading: 'idle',
  consultationError: null,
  consultationStatus: 'Completed',
  
  // Consultation details
  detailedConsultation: null,
  detailLoading: 'idle',
  detailError: null,
  selectedConsultation: null,
  
  // Master data
  labServiceMaster: {},
  packageMaster: {},
  packageServicesMapping: {},
  masterLoading: 'idle',
  masterError: null,
};

// --- Slice Definition ---
const patientReportsSlice = createSlice({
  name: 'patientReports',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setSelectedPatient: (state, action) => {
      state.selectedPatient = action.payload;
    },
    setSelectedConsultation: (state, action) => {
      state.selectedConsultation = action.payload;
    },
    setConsultationStatus: (state, action) => {
      state.consultationStatus = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.consultationError = null;
      state.detailError = null;
    },
    clearPatients: (state) => {
      state.patients = [];
      state.searchResults = [];
      state.selectedPatient = null;
      state.searchTerm = '';
      state.loading = 'idle';
    },
    clearConsultations: (state) => {
      state.consultations = [];
      state.consultationLoading = 'idle';
      state.consultationError = null;
    },
    clearDetailedConsultation: (state) => {
      state.detailedConsultation = null;
      state.detailLoading = 'idle';
      state.detailError = null;
    },
    clearAllData: (state) => {
      return { ...initialState };
    }
  },
  extraReducers: (builder) => {
    builder
      // --- Search Patients ---
      .addCase(searchPatients.pending, (state) => {
        state.loading = 'loading';
        state.error = null;
      })
      .addCase(searchPatients.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        state.searchResults = action.payload || [];
        state.patients = action.payload || [];
      })
      .addCase(searchPatients.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
        state.searchResults = [];
        state.patients = [];
      })
      
      // --- Fetch Patient Consultations ---
      .addCase(fetchPatientConsultations.pending, (state) => {
        state.consultationLoading = 'loading';
        state.consultationError = null;
        state.consultations = [];
      })
      .addCase(fetchPatientConsultations.fulfilled, (state, action) => {
        state.consultationLoading = 'succeeded';
        
        const response = action.payload;
        const allConsultations = [];
        
        if (response && typeof response === 'object') {
          if (response.onlineResult1?.Consultations) {
            allConsultations.push(...response.onlineResult1.Consultations);
          }
          if (response.opResult1?.Consultations) {
            allConsultations.push(...response.opResult1.Consultations);
          }
          if (response.ipResult1?.Consultations) {
            allConsultations.push(...response.ipResult1.Consultations);
          }
          if (response.Consultations && Array.isArray(response.Consultations)) {
            allConsultations.push(...response.Consultations);
          }
          if (Array.isArray(response)) {
            allConsultations.push(...response);
          }
        }
        
        state.consultations = allConsultations;
      })
      .addCase(fetchPatientConsultations.rejected, (state, action) => {
        state.consultationLoading = 'failed';
        state.consultationError = action.payload;
        state.consultations = [];
      })
      
      // --- Fetch Consultation Details ---
      .addCase(fetchConsultationDetails.pending, (state) => {
        state.detailLoading = 'loading';
        state.detailError = null;
        state.detailedConsultation = null;
      })
      .addCase(fetchConsultationDetails.fulfilled, (state, action) => {
        state.detailLoading = 'succeeded';
        state.detailedConsultation = action.payload;
      })
      .addCase(fetchConsultationDetails.rejected, (state, action) => {
        state.detailLoading = 'failed';
        state.detailError = action.payload;
        state.detailedConsultation = null;
      })
      
      // --- Fetch Lab Service Master Data ---
      .addCase(fetchLabServiceMasterData.pending, (state) => {
        state.masterLoading = 'loading';
        state.masterError = null;
      })
      .addCase(fetchLabServiceMasterData.fulfilled, (state, action) => {
        state.masterLoading = 'succeeded';
        state.labServiceMaster = action.payload;
      })
      .addCase(fetchLabServiceMasterData.rejected, (state, action) => {
        state.masterLoading = 'failed';
        state.masterError = action.payload;
        state.labServiceMaster = {};
      })
      
      // --- Fetch Package Master Data ---
      .addCase(fetchPackageMasterData.pending, (state) => {
        state.masterLoading = 'loading';
        state.masterError = null;
      })
      .addCase(fetchPackageMasterData.fulfilled, (state, action) => {
        state.masterLoading = 'succeeded';
        state.packageMaster = action.payload;
      })
      .addCase(fetchPackageMasterData.rejected, (state, action) => {
        state.masterLoading = 'failed';
        state.masterError = action.payload;
        state.packageMaster = {};
      })
      
      // --- Fetch Package Services Mapping ---
      .addCase(fetchPackageServicesMapping.fulfilled, (state, action) => {
        state.packageServicesMapping = action.payload;
      });
  }
});

// --- Export Actions ---
export const {
  setSearchTerm,
  setSelectedPatient,
  setSelectedConsultation,
  setConsultationStatus,
  clearError,
  clearPatients,
  clearConsultations,
  clearDetailedConsultation,
  clearAllData
} = patientReportsSlice.actions;

// --- Export Selectors ---
export const selectPatients = (state) => state.patientReports.patients;
export const selectSearchResults = (state) => state.patientReports.searchResults;
export const selectSelectedPatient = (state) => state.patientReports.selectedPatient;
export const selectConsultations = (state) => state.patientReports.consultations;
export const selectConsultationStatus = (state) => state.patientReports.consultationStatus;
export const selectDetailedConsultation = (state) => state.patientReports.detailedConsultation;
export const selectLoading = (state) => state.patientReports.loading;
export const selectConsultationLoading = (state) => state.patientReports.consultationLoading;
export const selectDetailLoading = (state) => state.patientReports.detailLoading;
export const selectError = (state) => state.patientReports.error;
export const selectConsultationError = (state) => state.patientReports.consultationError;
export const selectDetailError = (state) => state.patientReports.detailError;

// --- Default Export ---
export default patientReportsSlice.reducer;